// WB-M2A S2/S3 (doc 31 §12.7; Technical Lead authorization 2026-10-09) --
// Board → AI Tutor برنا: the two kinds of help, the visible message, and the
// Borna-only share descriptor.
//
// AT-API-020-H4 (API 0.2.0; doc 34/01 §3.1, §3.5; doc 34/02 §3): the two
// optional intents name a course ACTION of the 0.2.0 contract -- the only
// thing the turn DTO can carry beside text and the picture. The old 0.1
// `mode` / `intake` request is gone (Subway drops it, doc 34/01 §3.1):
//
//   hint     «راهنمایی بده»            `actionId: 'solve_exercise'` -- the
//                                       request of a Student stuck on a
//                                       step. Whether the Tutor withholds
//                                       the full solution is the course's
//                                       own policy for that action; nothing
//                                       here claims it.
//   explain  «این قسمت را توضیح بده»   `actionId: 'explain_topic'`.
//
// Whether the action really travels is decided by the chat store against
// the course's catalog (the AT-API-020-H1 rule, `resolveActionId`): an id
// the course does not list is never sent, and the share then travels as
// text + picture with the very same visible message. Nothing else changes
// for an unlisted action -- no notice, no second request.
//
// `check_answer` is deliberately NOT offered (doc 31 §12.4, §13 risk 1;
// doc 34/07 D-04): free board work can be judged against a wrongly matched
// book exercise. `BOARD_SHARE_ACTION_IDS` is the Board boundary the chat
// store enforces on ANY explicit request that travels with a share, before
// the catalog is consulted -- Subway refuses the rest as well (doc 34/01
// §3.5), but a refused share would stay on screen as failed.
//
// The message is always the Student's own words when they wrote any; else
// the visible default text of the chosen help; else a generic visible
// default. Never empty (a picture without text is refused upstream, doc 31
// §12.2), never a hidden machine prompt: what the sheet shows is what goes.

export const BOARD_SHARE_INTENTS = Object.freeze(['hint', 'explain'])

/**
 * AT-API-020-H4 (doc 34/01 §3.5) -- the only actions a Board share may ask
 * for. Anything else (`check_answer` first of all) never travels with a
 * share.
 */
export const BOARD_SHARE_ACTION_IDS = Object.freeze([
  'solve_exercise',
  'explain_topic',
])

const INTENT_ACTIONS = Object.freeze({
  hint: 'solve_exercise',
  explain: 'explain_topic',
})

/**
 * @param {unknown} value
 * @returns {boolean} whether `value` is an action a Board share may carry
 */
export function isBoardShareActionId(value) {
  return typeof value === 'string' && BOARD_SHARE_ACTION_IDS.includes(value)
}

/**
 * AT-API-020-H4 -- the 0.2.0 request of a chosen kind of help.
 * @param {string|null} intent
 * @returns {{actionId: string}|null} the action the intent names, or null
 *   for plain text (no chip, or an intent this sheet does not offer)
 */
export function boardShareRequest(intent) {
  const actionId =
    typeof intent === 'string' &&
    Object.prototype.hasOwnProperty.call(INTENT_ACTIONS, intent)
      ? INTENT_ACTIONS[intent]
      : null
  return actionId ? { actionId } : null
}

/**
 * The exact text sent with the picture.
 * @param {{question?: string, intent?: string|null,
 *   defaults: {generic: string, hint?: string, explain?: string}}} input
 * @returns {string}
 */
export function boardShareMessage({ question, intent, defaults }) {
  const typed = typeof question === 'string' ? question.trim() : ''
  if (typed) return typed
  const chosen = intent && defaults[intent]
  if (typeof chosen === 'string' && chosen.trim()) return chosen.trim()
  return (defaults.generic || '').trim()
}

/**
 * The Borna-only descriptor that travels beside the turn to Subway and is
 * stripped there before anything goes upstream (doc 31 §7). It names no
 * conversation and no board: Subway resolves both from the Student and the
 * session itself. The picture's filename is the turn's own attachment.
 * @param {{shareId: string, scope: string, elementIds: ReadonlyArray<string>,
 *   bounds: object, sceneVersion: number}} share -- the frozen share
 * @param {number|null} boardVersion -- the board version that holds the
 *   picture's scene, or null when the board could not be saved before the
 *   send (a transient failure; a stale board never gets this far)
 * @returns {{shareId: string, scope: string, elementIds: string[],
 *   bounds: object, sceneVersion: number, boardVersion: number|null}}
 */
export function toBoardShareDescriptor(share, boardVersion) {
  return {
    shareId: share.shareId,
    scope: share.scope,
    elementIds: Array.from(share.elementIds || []),
    bounds: { ...(share.bounds || {}) },
    sceneVersion: share.sceneVersion,
    boardVersion: Number.isInteger(boardVersion) ? boardVersion : null,
  }
}

/**
 * The share records Subway returns on a conversation read (`boardShares`),
 * by the Student turn each one belongs to. Anything without a turn id is a
 * share that never reached the Tutor and has no badge.
 * @param {object[]|undefined} shares
 * @returns {Object.<string, {shareId: string, scope: string,
 *   snapshotFilename: string|null, createdAt: string|null}>}
 */
export function toBoardShareByTurnId(shares) {
  const out = {}
  if (!Array.isArray(shares)) return out
  shares.forEach(item => {
    if (!item || typeof item !== 'object') return
    if (typeof item.studentTurnId !== 'string' || !item.studentTurnId) return
    out[item.studentTurnId] = {
      shareId: typeof item.shareId === 'string' ? item.shareId : '',
      scope: item.scope === 'viewport' ? 'viewport' : 'selection',
      snapshotFilename:
        typeof item.snapshotFilename === 'string'
          ? item.snapshotFilename
          : null,
      createdAt: typeof item.createdAt === 'string' ? item.createdAt : null,
    }
  })
  return out
}
